import React, { useEffect, useRef, useState, useCallback } from 'react';
import type { Cidade, Estado, Rede } from '../types';
import { buscarMunicipios, listarEstados, listarRedes, criarMunicipio, geocodeCity } from '../types';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

//#region AC_BLOCK cidade-step -- Etapa de seleção de cidade com filtro de rede escolar, cache, geolocalização concorrente GPS/IP e mapa de cadastro
// @ac-purpose Permite selecionar a rede de ensino, pesquisar e cadastrar municípios com geocode IP/GPS
// @ac-related react-app/src/features/sim-onboarding/types.ts#buscar-municipios

// Ícone personalizado para o marcador do cadastro de nova cidade
const markerPinIcon = L.divIcon({
  className: '',
  html: `<div style="font-size: 30px; transform: translate(-15px, -30px); filter: drop-shadow(0 4px 6px rgba(0,0,0,0.4));">📍</div>`,
  iconSize: [30, 30],
  iconAnchor: [0, 0]
});

// Componente para centralizar o mapa no cadastro de nova cidade
function MapCenterController({ center }: { center: [number, number] }) {
  const map = useMap();
  const lat = center[0];
  const lng = center[1];
  useEffect(() => {
    if (isFinite(lat) && isFinite(lng)) {
      map.setView([lat, lng], 13);
    }
  }, [lat, lng, map]);
  return null;
}

// Componente para capturar cliques no mapa e atualizar as coordenadas no cadastro
function MapClickEvents({ onClick }: { onClick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onClick(e.latlng.lat, e.latlng.lng);
    }
  });
  return null;
}

export interface CidadeStepProps {
  selecionada: Cidade | null;
  onSelect: (c: Cidade) => void;
  onClear: () => void;
  onNext: () => void;
}

export function CidadeStep({ selecionada, onSelect, onClear, onNext }: CidadeStepProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Cidade[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoSugestao, setGeoSugestao] = useState<Cidade | null>(null);
  const [geoSource, setGeoSource] = useState<'GPS' | 'IP' | null>(null);
  const [hideSugestao, setHideSugestao] = useState(false); // Permite ocultar permanentemente a sugestão
  const [showNova, setShowNova] = useState(false);
  const [estados, setEstados] = useState<Estado[]>([]);
  const [redes, setRedes] = useState<Rede[]>([]);
  const [selectedRedeId, setSelectedRedeId] = useState<string>('');
  
  const [novaNome, setNovaNome] = useState('');
  const [novaEstadoId, setNovaEstadoId] = useState('');
  const [novaLat, setNovaLat] = useState('');
  const [novaLng, setNovaLng] = useState('');
  const [novaSaving, setNovaSaving] = useState(false);
  const [novaGeoLoading, setNovaGeoLoading] = useState(false);
  const [erro, setErro] = useState('');
  
  const [searchAddress, setSearchAddress] = useState('');
  const [resolvedCoords, setResolvedCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [resolvingGeo, setResolvingGeo] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cache em memória persistido na ref do componente: "redeId:query" -> Cidade[]
  const cacheRef = useRef<Record<string, Cidade[]>>({});

  // Foca no input de busca se a cidade não estiver selecionada e a sugestão já tiver sido descartada/ocultada
  useEffect(() => {
    if (!selecionada && (hideSugestao || !geoSugestao) && !geoLoading && !showNova) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [selecionada, hideSugestao, geoSugestao, geoLoading, showNova]);

  // Sincroniza estado selecionado quando o formulário de cadastro abre para uma cidade geolocalizada
  useEffect(() => {
    if (showNova) {
      const isGeolocatedRegister = !!geoSugestao && !geoSugestao.id;
      if (isGeolocatedRegister && geoSugestao) {
        const matched = estados.find(e => e.sigla === geoSugestao.uf && e.redeId === selectedRedeId);
        if (matched) {
          setNovaEstadoId(matched.id);
        } else {
          setNovaEstadoId('');
        }
      }
    }
  }, [showNova, geoSugestao, estados, selectedRedeId]);

  // Carrega redes, estados e roda geolocalização concorrente GPS/IP
  // REGRA DE PRIORIDADE (imutável):
  //   GPS > IP, sempre. Se GPS já resolveu, IP jamais sobrescreve.
  //   Se IP resolveu primeiro (mais rápido), GPS ainda pode e DEVE sobrescrever quando chegar.
  //   IP só exibe resultado definitivo se GPS falhou/timeout E GPS ainda não resolveu.
  useEffect(() => {
    setGeoLoading(true);

    // gpsResolved: GPS já retornou com sucesso (tem prioridade absoluta)
    // gpsSettled: GPS terminou de uma forma ou outra (sucesso ou erro)
    let gpsResolved = false;
    let gpsSettled = false;
    let initialRedeId = '';
    let estadosDataGlobal: Estado[] = [];

    // Função central que aplica o resultado na tela.
    // source='GPS' sempre substitui qualquer resultado anterior.
    // source='IP' só aplica se GPS ainda não resolveu.
    const applyGeoResult = async (
      lat: number,
      lng: number,
      source: 'GPS' | 'IP',
      name?: string,
      stateCode?: string,
    ) => {
      // IP não pode sobrescrever GPS — regra absoluta
      if (source === 'IP' && gpsResolved) return;

      try {
        let cityName = name;
        let uf = stateCode;

        // GPS precisa de reverse geocode para obter nome/UF
        if (!cityName || !uf) {
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
            { headers: { 'Accept-Language': 'pt-BR' } },
          );
          if (r.ok) {
            const data = await r.json();
            cityName =
              data.address?.city ||
              data.address?.town ||
              data.address?.municipality ||
              data.address?.village ||
              data.address?.suburb;
            uf = data.address?.state_code?.replace('BR-', '') || '';
          }
        }

        // Após o await do Nominatim, verifica novamente:
        // se veio do IP e o GPS já resolveu enquanto aguardávamos, descarta
        if (source === 'IP' && gpsResolved) return;

        if (cityName && uf) {
          let found = await buscarMunicipios(cityName, initialRedeId);
          if (found.length === 0) {
            const cityNameSemAcento = cityName.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            if (cityNameSemAcento !== cityName) {
              found = await buscarMunicipios(cityNameSemAcento, initialRedeId);
            }
          }

          // Última checagem: não deixa IP sobrescrever GPS
          if (source === 'IP' && gpsResolved) return;

          const match = found.find(c => c.uf === uf);

          if (match) {
            setGeoSugestao(match);
          } else {
            const stateObj = estadosDataGlobal.find(e => e.sigla === uf);
            setGeoSugestao({
              id: '',
              nome: cityName,
              uf: uf,
              estadoId: stateObj ? stateObj.id : '',
              lat,
              lng,
              temGestor: false,
              totalEscolas: 0,
            });
          }

          setGeoSource(source);
          if (source === 'GPS') gpsResolved = true;
          setGeoLoading(false);
        } else {
          setGeoLoading(false);
        }
      } catch (err) {
        console.error(`Erro na geocodificação (${source}):`, err);
        setGeoLoading(false);
      }
    };

    // --- IP: dispara imediatamente em paralelo ---
    // Gerencia o próprio status de loading de forma a liberar a tela caso o IP falhe rapidamente.
    const ipPromise = fetch('https://ipapi.co/json/')
      .then(res => (res.ok ? res.json() : null))
      .then(async ipData => {
        if (ipData && !gpsResolved) {
          await applyGeoResult(
            ipData.latitude,
            ipData.longitude,
            'IP',
            ipData.city,
            ipData.region_code,
          );
        } else {
          setGeoLoading(false);
        }
        return ipData;
      })
      .catch(() => {
        setGeoLoading(false);
        return null;
      });

    // --- GPS: dispara imediatamente; tem prioridade absoluta ---
    // Roda de forma silenciosa em background. Só atualiza a tela se obtiver sucesso.
    // Se falhar (timeout ou negado), não altera a UI e deixa o fluxo do IP/busca seguir normalmente.
    const obterGPS = () => {
      if (!navigator.geolocation) {
        gpsSettled = true;
        return;
      }

      const onSuccess = async (pos: GeolocationPosition) => {
        gpsSettled = true;
        await applyGeoResult(pos.coords.latitude, pos.coords.longitude, 'GPS');
      };

      const onError = async (err: GeolocationPositionError) => {
        // code 3 = TIMEOUT — desktop sem GPS físico.
        // Tenta estágio 2 com localização de rede (rápida, ~1s).
        if (err.code === 3) {
          console.info('[GPS] Timeout na alta precisão — tentando localização de rede silenciosamente...');
          navigator.geolocation.getCurrentPosition(
            async pos => {
              gpsSettled = true;
              await applyGeoResult(pos.coords.latitude, pos.coords.longitude, 'GPS');
            },
            async () => {
              gpsSettled = true;
              console.warn('[GPS] Localização de rede também falhou. Mantendo estado atual.');
              // Falhou: não faz nada, deixa a sugestão por IP ou busca manual ativa
            },
            { timeout: 6000, enableHighAccuracy: false, maximumAge: 60000 },
          );
        } else {
          gpsSettled = true;
          console.warn('[GPS] Falhou (code', err.code, '). Mantendo estado atual.');
          // Falhou: não faz nada
        }
      };

      navigator.geolocation.getCurrentPosition(onSuccess, onError, {
        timeout: 8000,
        enableHighAccuracy: true,
        maximumAge: 30000, // aceita cache de até 30s
      });
    };

    async function init() {
      try {
        const [redesData, estadosData] = await Promise.all([
          listarRedes(),
          listarEstados(),
        ]);

        setRedes(redesData);
        setEstados(estadosData);
        estadosDataGlobal = estadosData;

        if (redesData.length > 0) {
          initialRedeId = redesData[0].id;
          setSelectedRedeId(initialRedeId);
        }

        // GPS dispara imediatamente
        obterGPS();

        // Monitora mudança de permissão: se o usuário conceder durante o fluxo,
        // força nova chamada GPS que substituirá qualquer resultado anterior de IP
        if (navigator.permissions && typeof navigator.permissions.query === 'function') {
          navigator.permissions
            .query({ name: 'geolocation' as PermissionName })
            .then(permissionStatus => {
              permissionStatus.onchange = () => {
                if (permissionStatus.state === 'granted') {
                  // Reset para que o GPS possa ganhar novamente
                  gpsResolved = false;
                  gpsSettled = false;
                  obterGPS();
                }
              };
            })
            .catch(() => {});
        }
      } catch (err) {
        console.error('Erro na inicialização:', err);
        setGeoLoading(false);
      }
    }

    init();
     
  }, []);

  // Busca automática com debounce de 350ms usando cache segregado por rede
  const handleQuery = useCallback((v: string, redeId: string) => {
    setQuery(v);
    
    const term = v.trim();
    if (term.length < 2) {
      setResults([]);
      setSearchPerformed(false);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      return;
    }

    setSearchPerformed(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const cacheKey = `${redeId}:${term.toLowerCase()}`;
    
    // Cache Hit
    if (cacheRef.current[cacheKey] !== undefined) {
      setResults(cacheRef.current[cacheKey]);
      setSearchPerformed(true);
      return;
    }

    // Cache Miss -> Busca via API
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await buscarMunicipios(term, redeId);
        cacheRef.current[cacheKey] = res;
        setResults(res);
      } catch {}
      setLoading(false);
      setSearchPerformed(true);
    }, 350);
  }, []);

  // Busca manual (botão) que ignora o cache e força nova consulta
  const handleSearchManual = async () => {
    const term = query.trim();
    if (term.length < 2) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    setLoading(true);
    setSearchPerformed(false);
    
    const cacheKey = `${selectedRedeId}:${term.toLowerCase()}`;
    try {
      const res = await buscarMunicipios(term, selectedRedeId);
      cacheRef.current[cacheKey] = res;
      setResults(res);
    } catch {}
    
    setLoading(false);
    setSearchPerformed(true);
  };

  // Acionado ao clicar em "Usar" / "Cadastrar e Usar" na localização sugerida
  const handleUsarSugestao = async () => {
    if (!geoSugestao) return;
    if (geoSugestao.id) {
      // Cidade já cadastrada — usa direto
      onSelect(geoSugestao);
    } else {
      // Cidade detectada mas não cadastrada — salva diretamente (sem abrir formulário)
      // Já temos: nome, UF, lat, lng da geolocalização
      setNovaSaving(true);
      try {
        const uf = geoSugestao.uf;
        const cidade = await criarMunicipio({
          nome:        geoSugestao.nome,
          estadoSigla: uf || undefined,
          redeId:      selectedRedeId || undefined,
          lat:         geoSugestao.lat ?? 0,
          lng:         geoSugestao.lng ?? 0,
        });
        onSelect(cidade);
      } catch (e: any) {
        setErro(e.message ?? 'Erro ao cadastrar cidade.');
      }
      setNovaSaving(false);
    }
  };

  // Cadastro rápido para a busca manual (geocodifica, salva e avança diretamente em um clique)
  const handleCadastrarRapido = async () => {
    setErro('');
    setNovaSaving(true);
    try {
      const q = encodeURIComponent(query.trim());
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&countrycodes=br&addressdetails=1`,
        { headers: { 'Accept-Language': 'pt-BR' } }
      );
      if (!r.ok) throw new Error('Não foi possível conectar ao serviço de mapas.');
      const data = await r.json();
      
      // Fallback 1: se não achar nada no geocode, abre o form manual para preenchimento
      if (data.length === 0) {
        setNovaNome(query);
        setShowNova(true);
        setNovaSaving(false);
        return;
      }

      const item = data[0];
      const lat = parseFloat(item.lat);
      const lng = parseFloat(item.lon);
      const cityName = item.address?.city || item.address?.town || item.address?.municipality || item.address?.village || item.address?.suburb || query.trim();
      
      // Extração robusta da sigla do estado (UF) para o Brasil
      const address = item.address || {};
      const isoCode = address['ISO3166-2-lvl4'] || address.state_code || '';
      let uf = isoCode.replace('BR-', '').trim().toUpperCase();

      // Fallback por nome do estado caso a sigla não venha em formato ISO
      if (!uf && address.state) {
        const ESTADOS_MAP: Record<string, string> = {
          'Acre': 'AC', 'Alagoas': 'AL', 'Amapá': 'AP', 'Amazonas': 'AM', 'Bahia': 'BA',
          'Ceará': 'CE', 'Distrito Federal': 'DF', 'Espírito Santo': 'ES', 'Goiás': 'GO',
          'Maranhão': 'MA', 'Mato Grosso': 'MT', 'Mato Grosso do Sul': 'MS', 'Minas Gerais': 'MG',
          'Pará': 'PA', 'Paraíba': 'PB', 'Paraná': 'PR', 'Pernambuco': 'PE', 'Piauí': 'PI',
          'Rio de Janeiro': 'RJ', 'Rio Grande do Norte': 'RN', 'Rio Grande do Sul': 'RS',
          'Rondônia': 'RO', 'Roraima': 'RR', 'Santa Catarina': 'SC', 'São Paulo': 'SP',
          'Sergipe': 'SE', 'Tocantins': 'TO'
        };
        uf = ESTADOS_MAP[address.state] || '';
      }

      // Fallback 2: se dados forem inconsistentes, abre o form manual
      if (!cityName || !uf || isNaN(lat) || isNaN(lng)) {
        console.warn('[Cadastro Rápido] Dados insuficientes obtidos da API de mapas:', { cityName, uf, lat, lng });
        setNovaNome(query);
        setShowNova(true);
        setNovaSaving(false);
        return;
      }

      const stateObj = estados.find(e => e.sigla === uf && e.redeId === selectedRedeId);
      
      const cidade = await criarMunicipio({
        nome: cityName,
        estadoId: stateObj ? stateObj.id : undefined,
        estadoSigla: stateObj ? undefined : uf,
        redeId: selectedRedeId || undefined,
        lat,
        lng,
      });

      onSelect(cidade);
    } catch (e: any) {
      console.warn('Falha no cadastro rápido. Redirecionando para formulário manual:', e);
      // Fallback 3: qualquer falha na API ou conexão abre o form manual como segurança
      setNovaNome(query);
      setShowNova(true);
    } finally {
      setNovaSaving(false);
    }
  };

  // Geocode automático ao preencher nome+estado no form nova cidade
  const handleGeocodarNova = useCallback(async () => {
    // Se for uma cidade localizada via geocode IP/GPS, a posição e nome são fixos, não geocoda no blur
    const isGeolocatedRegister = !!geoSugestao && !geoSugestao.id;
    if (isGeolocatedRegister) return;

    if (!novaNome || !novaEstadoId) return;
    const uf = estados.find(e => e.id === novaEstadoId)?.sigla ?? '';
    if (!uf) return;
    setNovaGeoLoading(true);
    const coords = await geocodeCity(novaNome, uf);
    if (coords) { setNovaLat(String(coords.lat)); setNovaLng(String(coords.lng)); }
    setNovaGeoLoading(false);
  }, [novaNome, novaEstadoId, estados, geoSugestao]);

  const handleSalvarNova = async () => {
    setErro('');
    if (!novaNome.trim()) return setErro('Nome da cidade é obrigatório.');
    
    // Se for registro de cidade localizada por GPS/IP, passamos estadoSigla e redeId
    const isGeolocatedRegister = !!geoSugestao && !geoSugestao.id;
    if (!novaEstadoId && !isGeolocatedRegister) return setErro('Selecione um estado.');
    if (!novaLat || !novaLng) return setErro('Coordenadas geográficas obrigatórias.');

    setNovaSaving(true);
    try {
      const cidade = await criarMunicipio({
        nome: novaNome.trim(),
        estadoId: novaEstadoId || undefined,
        estadoSigla: isGeolocatedRegister ? geoSugestao.uf : undefined,
        redeId: isGeolocatedRegister ? selectedRedeId : undefined,
        lat: parseFloat(novaLat),
        lng: parseFloat(novaLng),
      });
      onSelect(cidade);
      setShowNova(false);
    } catch (e: any) {
      setErro(e.message ?? 'Erro ao salvar cidade.');
    }
    setNovaSaving(false);
  };

  useEffect(() => {
    if (selecionada) {
      setSearchAddress(`${selecionada.nome}, ${selecionada.uf}`);
      if (selecionada.lat && selecionada.lng) {
        setResolvedCoords({ lat: selecionada.lat, lng: selecionada.lng });
      } else {
        setResolvedCoords(null);
      }
    } else {
      setSearchAddress('');
      setResolvedCoords(null);
    }
  }, [selecionada]);

  const handleGeocodeSelectedCity = async () => {
    if (!searchAddress.trim()) {
      setErro('Digite um endereço ou nome de cidade.');
      return;
    }
    setErro('');
    setResolvingGeo(true);
    try {
      const q = encodeURIComponent(searchAddress.trim());
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&countrycodes=br`,
        { headers: { 'Accept-Language': 'pt-BR' } }
      );
      if (!r.ok) throw new Error('Não foi possível conectar ao serviço de mapas.');
      const data = await r.json();
      if (data && data.length > 0) {
        setResolvedCoords({
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon)
        });
      } else {
        setErro('Localização não encontrada para o endereço informado.');
      }
    } catch (err: any) {
      setErro(err.message ?? 'Erro ao buscar localização.');
    } finally {
      setResolvingGeo(false);
    }
  };

  const handleSaveCoordsAndNext = async () => {
    if (!selecionada || !resolvedCoords) return;
    setErro('');
    setNovaSaving(true);
    try {
      const updated = await criarMunicipio({
        nome: selecionada.nome,
        estadoSigla: selecionada.uf,
        redeId: selectedRedeId || undefined,
        lat: resolvedCoords.lat,
        lng: resolvedCoords.lng,
      });
      onSelect(updated);
      onNext();
    } catch (e: any) {
      setErro(e.message ?? 'Erro ao salvar coordenadas no município.');
    } finally {
      setNovaSaving(false);
    }
  };

  const naoEncontrada = query.trim().length > 2 && results.length === 0 && !loading && !selecionada && searchPerformed;

  // Determina as coordenadas centrais do mapa de cadastro
  const mapCenter: [number, number] = [
    parseFloat(novaLat) || -19.9191,
    parseFloat(novaLng) || -43.9386
  ];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      {/* Header */}
      {!showNova && (
        <div>
          <div style={{ fontSize:11,fontWeight:700,color:'#7c3aed',letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:10 }}>Etapa 2 de 4</div>
          <h1 style={{ fontSize:36,fontWeight:900,color:'#fff',letterSpacing:'-0.03em',lineHeight:1.1,margin:0 }}>Onde será a simulação?</h1>
          <p style={{ fontSize:14,color:'#94a3b8',marginTop:8,lineHeight:1.6 }}>
            Busque uma cidade cadastrada no sistema ou cadastre uma nova.
          </p>
        </div>
      )}

      {/* 1. Loader da Geolocalização concorrente */}
      {geoLoading && !showNova && (
        <div style={{ padding:'12px 16px',borderRadius:12,background:'rgba(96,165,250,0.06)',border:'1px solid rgba(96,165,250,0.2)',display:'flex',alignItems:'center',gap:10,animation:'fadeUp .4s ease' }}>
          <span style={{ fontSize:16,animation:'pulse 1.5s ease-in-out infinite' }}>📡</span>
          <span style={{ fontSize:13,color:'#93c5fd' }}>Identificando região…</span>
        </div>
      )}
      
      {/* 2. Exibe o card detectado (Se NÃO está carregando, NÃO está selecionada, NÃO está ocultada e NÃO está cadastrando) */}
      {!geoLoading && geoSugestao && !selecionada && !hideSugestao && !showNova && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Card detectado */}
          <div style={{ padding:'3px',borderRadius:14,background:'linear-gradient(135deg,rgba(96,165,250,0.15),rgba(124,58,237,0.1))',border:'1px solid rgba(96,165,250,0.3)' }}>
            <div style={{ padding:'12px 14px',display:'flex',alignItems:'center',gap:12,background:'rgba(10,8,34,0.6)',borderRadius:12 }}>
              <span style={{ fontSize:20,flexShrink:0 }}>📍</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:10,fontWeight:700,color:'#60a5fa',textTransform:'uppercase',letterSpacing:'0.1em' }}>
                  Região detectada {geoSource ? `(${geoSource})` : ''}
                </div>
                <div style={{ fontSize:16,fontWeight:800,color:'#f1f5f9' }}>{geoSugestao.nome} — {geoSugestao.uf}</div>
                <div style={{ fontSize:10,color:'#94a3b8',marginTop:1 }}>
                  {geoSugestao.id ? 'Já possui cadastro no sistema' : '⚠️ Pendente de cadastro'}
                </div>
              </div>
              <button
                onClick={handleUsarSugestao}
                disabled={novaSaving}
                style={{
                  padding:'8px 16px', borderRadius:9, border:'none',
                  cursor: novaSaving ? 'wait' : 'pointer',
                  background: novaSaving
                    ? 'rgba(99,102,241,0.4)'
                    : 'linear-gradient(135deg,#3b82f6,#7c3aed)',
                  color:'#fff', fontSize:12, fontWeight:700, flexShrink:0,
                  display:'flex', alignItems:'center', gap:6,
                  opacity: novaSaving ? 0.7 : 1,
                }}
              >
                {novaSaving ? (
                  <>
                    <span style={{ width:10,height:10,borderRadius:'50%',border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'#fff',display:'inline-block',animation:'spin 0.7s linear infinite' }} />
                    Salvando...
                  </>
                ) : (
                  geoSugestao.id ? 'Usar →' : 'Cadastrar e Usar →'
                )}
              </button>
            </div>
          </div>
          
          {/* Botão Outra Cidade - Destacado e em tom amarelo conforme solicitado */}
          <button
            onClick={() => {
              setHideSugestao(true);
            }}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: 12,
              border: '1px solid rgba(251,191,36,0.4)',
              background: 'linear-gradient(135deg, rgba(251,191,36,0.15), rgba(217,119,6,0.25))',
              color: '#fbbf24',
              fontSize: 14,
              fontWeight: 800,
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 4px 15px rgba(251,191,36,0.15)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.filter = 'brightness(1.2)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(251,191,36,0.25)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.filter = '';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(251,191,36,0.15)';
            }}
          >
            Outra Cidade
          </button>
        </div>
      )}

      {/* 3. Cidade selecionada (se houver) */}
      {selecionada && (
        <div style={{ padding:'3px',borderRadius:14,background:'linear-gradient(135deg,rgba(124,58,237,0.2),rgba(96,165,250,0.15))',border:'1px solid rgba(124,58,237,0.45)' }}>
          <div style={{ padding:'16px',background:'rgba(10,8,34,0.6)',borderRadius:12 }}>
            <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between' }}>
              <div>
                <div style={{ fontSize:10,fontWeight:700,color:'#a78bfa',textTransform:'uppercase',letterSpacing:'0.1em' }}>Cidade Selecionada</div>
                <div style={{ fontSize:22,fontWeight:900,color:'#f1f5f9',marginTop:3 }}>{selecionada.nome}</div>
                <div style={{ fontSize:12,color:'#94a3b8',marginTop:2 }}>
                  {selecionada.uf}
                  {selecionada.totalEscolas !== undefined && ` · ${selecionada.totalEscolas} escola(s)`}
                  {resolvedCoords && ` · ${resolvedCoords.lat.toFixed(4)}, ${resolvedCoords.lng.toFixed(4)}`}
                </div>
                {!selecionada.temGestor && (
                  <div style={{ marginTop:6,fontSize:12,color:'#fbbf24',fontWeight:600 }}>
                    ⚠️ Esta cidade ainda não tem Gestor Municipal — próxima etapa irá criar.
                  </div>
                )}
              </div>
              <button onClick={onClear} style={{ background:'transparent',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'#94a3b8',fontSize:12,cursor:'pointer',padding:'6px 12px' }}>
                Trocar
              </button>
            </div>

            {/* Caso NÃO tenha coordenadas válidas (lat/lng nulas ou zero) */}
            {!resolvedCoords ? (
              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 16 }}>
                <div style={{ fontSize: 13, color: '#fbbf24', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  ⚠️ Município sem coordenadas (lat/lng) cadastradas!
                </div>
                
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={searchAddress}
                    onChange={e => setSearchAddress(e.target.value)}
                    placeholder="Digite o endereço ou cidade..."
                    style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#f1f5f9', fontSize: 13, outline: 'none' }}
                  />
                  <button
                    onClick={handleGeocodeSelectedCity}
                    disabled={resolvingGeo}
                    style={{ padding: '0 16px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#3b82f6,#7c3aed)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                  >
                    {resolvingGeo ? 'Buscando...' : 'Buscar'}
                  </button>
                </div>

                {erro && <div style={{ fontSize: 12, color: '#f43f5e', fontWeight: 600 }}>{erro}</div>}
              </div>
            ) : (
              /* Se tem coordenadas resolvidas (mas não tinha no banco originalmente) */
              (!selecionada.lat || !selecionada.lng) && (
                <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 16 }}>
                  <div style={{ fontSize: 13, color: '#10b981', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                    ✅ Localização identificada!
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      value={searchAddress}
                      onChange={e => setSearchAddress(e.target.value)}
                      style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#f1f5f9', fontSize: 13, outline: 'none' }}
                    />
                    <button
                      onClick={handleGeocodeSelectedCity}
                      disabled={resolvingGeo}
                      style={{ padding: '0 16px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#3b82f6,#7c3aed)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                    >
                      {resolvingGeo ? 'Buscando...' : 'Alterar'}
                    </button>
                  </div>
                  
                  {/* Mini Mapa do Lado */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, height: 120 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4 }}>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>Coordenadas obtidas:</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Lat: {resolvedCoords.lat.toFixed(6)}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Lng: {resolvedCoords.lng.toFixed(6)}</div>
                    </div>
                    <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.12)' }}>
                      <MapContainer center={[resolvedCoords.lat, resolvedCoords.lng]} zoom={12} style={{ height: '100%', width: '100%' }} zoomControl={false}>
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <Marker position={[resolvedCoords.lat, resolvedCoords.lng]} icon={markerPinIcon} />
                        <MapCenterController center={[resolvedCoords.lat, resolvedCoords.lng]} />
                        <MapClickEvents onClick={(lat, lng) => setResolvedCoords({ lat, lng })} />
                      </MapContainer>
                    </div>
                  </div>
                </div>
              )
            )}

            {/* Botão de prosseguir/salvar */}
            {!resolvedCoords ? (
              <button disabled style={{ marginTop:14,width:'100%',padding:'14px',borderRadius:12,border:'none',background:'rgba(255,255,255,0.05)',color:'#64748b',fontSize:15,fontWeight:800,cursor:'not-allowed' }}>
                Localização Obrigatória para Continuar
              </button>
            ) : (
              (!selecionada.lat || !selecionada.lng) ? (
                <button onClick={handleSaveCoordsAndNext} disabled={novaSaving} style={{ marginTop:14,width:'100%',padding:'14px',borderRadius:12,border:'none',cursor:'pointer',background:'linear-gradient(135deg,#10b981,#059669)',color:'#fff',fontSize:15,fontWeight:800,boxShadow:'0 8px 24px rgba(16,185,129,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  {novaSaving ? 'Salvando...' : 'Salvar Coordenadas e Continuar →'}
                </button>
              ) : (
                <button onClick={onNext} style={{ marginTop:14,width:'100%',padding:'14px',borderRadius:12,border:'none',cursor:'pointer',background:'linear-gradient(135deg,#7c3aed,#4f46e5)',color:'#fff',fontSize:15,fontWeight:800,boxShadow:'0 8px 24px rgba(124,58,237,0.4)' }}>
                  Continuar →
                </button>
              )
            )}
          </div>
        </div>
      )}

      {/* 4. Caixa de pesquisa manual (exibida APENAS se geolocalização terminou, não há seleção, não está cadastrando e a sugestão foi descartada ou não existe) */}
      {!geoLoading && !selecionada && !showNova && (hideSugestao || !geoSugestao) && (
        <>
          {/* Campo de busca + Botão de pesquisa manual */}
          <div style={{ display:'flex', gap:8 }}>
            <div style={{ position:'relative', flex:1 }}>
              <span style={{ position:'absolute',left:14,top:'50%',transform:'translateY(-50%)',fontSize:16,opacity:.5 }}>🔍</span>
              <input
                ref={inputRef}
                value={query}
                onChange={e => handleQuery(e.target.value, selectedRedeId)}
                placeholder="Digite o nome da cidade..."
                style={{ width:'100%',padding:'14px 14px 14px 40px',borderRadius:12,border:'1px solid rgba(255,255,255,0.1)',background:'rgba(255,255,255,0.05)',color:'#f1f5f9',fontSize:15,outline:'none',boxSizing:'border-box' }}
              />
              {loading && <span style={{ position:'absolute',right:14,top:'50%',transform:'translateY(-50%)',fontSize:12,color:'#7c3aed' }}>...</span>}
            </div>
            <button
              onClick={handleSearchManual}
              disabled={loading || query.trim().length < 2}
              style={{
                padding: '0 20px',
                borderRadius: 12,
                border: '1px solid rgba(124,58,237,0.4)',
                background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
                cursor: (loading || query.trim().length < 2) ? 'not-allowed' : 'pointer',
                opacity: (loading || query.trim().length < 2) ? 0.5 : 1,
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { if (!loading && query.trim().length >= 2) e.currentTarget.style.filter = 'brightness(1.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.filter = ''; }}
            >
              Buscar
            </button>
          </div>

          {/* Resultados da busca — exibidos no estilo de card premium bem destacado */}
          {searchPerformed && results.length > 0 && (
            <div style={{ display:'flex',flexDirection:'column',gap:8,maxHeight:220,overflowY:'auto' }}>
              {results.map(c => (
                <div key={c.id} style={{ padding:'3px',borderRadius:14,background:'linear-gradient(135deg,rgba(96,165,250,0.15),rgba(124,58,237,0.1))',border:'1px solid rgba(96,165,250,0.3)' }}>
                  <div style={{ padding:'12px 14px',display:'flex',alignItems:'center',gap:12,background:'rgba(10,8,34,0.6)',borderRadius:12 }}>
                    <span style={{ fontSize:20,flexShrink:0 }}>🏙️</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:10,fontWeight:700,color:'#60a5fa',textTransform:'uppercase',letterSpacing:'0.1em' }}>Cidade Encontrada</div>
                      <div style={{ fontSize:16,fontWeight:800,color:'#f1f5f9' }}>{c.nome} — {c.uf}</div>
                      <div style={{ fontSize:10,color:'#94a3b8',marginTop:1 }}>
                        {c.totalEscolas} escola(s) cadastrada(s) {c.temGestor ? '✅ Gestor cadastrado' : '⚠️ Sem gestor'}
                      </div>
                    </div>
                    <button onClick={() => onSelect(c)}
                      style={{ padding:'8px 16px',borderRadius:9,border:'none',cursor:'pointer',background:'linear-gradient(135deg,#3b82f6,#7c3aed)',color:'#fff',fontSize:12,fontWeight:700,flexShrink:0 }}>
                      Selecionar →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Não encontrada — propõe cadastro no estilo de card premium bem destacado */}
          {naoEncontrada && !showNova && (
            <div style={{ padding:'3px',borderRadius:14,background:'linear-gradient(135deg,rgba(251,191,36,0.15),rgba(124,58,237,0.08))',border:'1px solid rgba(251,191,36,0.3)' }}>
              <div style={{ padding:'12px 14px',display:'flex',alignItems:'center',gap:12,background:'rgba(10,8,34,0.6)',borderRadius:12 }}>
                <span style={{ fontSize:20,flexShrink:0 }}>⚠️</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:10,fontWeight:700,color:'#fbbf24',textTransform:'uppercase',letterSpacing:'0.1em' }}>Cidade não cadastrada</div>
                  <div style={{ fontSize:16,fontWeight:800,color:'#f1f5f9' }}>{query}</div>
                  <div style={{ fontSize:10,color:'#94a3b8',marginTop:1 }}>
                    Cadastre esta cidade para continuar a simulação
                  </div>
                </div>
                <button 
                  onClick={handleCadastrarRapido}
                  disabled={novaSaving}
                  style={{ 
                    padding:'8px 16px',
                    borderRadius:9,
                    border:'none',
                    cursor: novaSaving ? 'wait' : 'pointer',
                    background:'linear-gradient(135deg,#fbbf24,#d97706)',
                    color:'#000',
                    fontSize:12,
                    fontWeight:800,
                    flexShrink:0,
                    display:'flex',
                    alignItems:'center',
                    gap:6,
                    opacity: novaSaving ? 0.7 : 1
                  }}
                >
                  {novaSaving ? (
                    <>
                      <span style={{ width:10,height:10,borderRadius:'50%',border:'2px solid rgba(0,0,0,0.3)',borderTopColor:'#000',display:'inline-block',animation:'spin 0.7s linear infinite' }} />
                      Salvando...
                    </>
                  ) : (
                    '+ Cadastrar →'
                  )}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Form de nova cidade lado a lado com o mapa interativo Leaflet */}
      {showNova && !selecionada && (
        <div style={{ padding:'3px',borderRadius:14,background:'linear-gradient(135deg,rgba(124,58,237,0.15),rgba(96,165,250,0.1))',border:'1px solid rgba(124,58,237,0.3)' }}>
          <div style={{ padding:'16px',background:'rgba(10,8,34,0.6)',borderRadius:12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>📍</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Cadastrar Nova Cidade</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#f1f5f9', marginTop: 1 }}>{novaNome || 'Preencha os dados abaixo'}</div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>
                  {novaEstadoId || (geoSugestao && !geoSugestao.id) ? `${estados.find(e => e.id === novaEstadoId)?.nome || geoSugestao?.uf || ''} · ${parseFloat(novaLat)?.toFixed(4) || '0.0000'}, ${parseFloat(novaLng)?.toFixed(4) || '0.0000'}` : 'Aguardando preenchimento…'}
                </div>
              </div>
            </div>

            {erro && <div style={{ fontSize:12,color:'#f87171',marginBottom:10,padding:'8px 12px',borderRadius:8,background:'rgba(239,68,68,0.08)' }}>{erro}</div>}

            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 16 }}>
              {/* Coluna Esquerda: Formulário de Entrada */}
              <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>Nome da Cidade</label>
                <input 
                  value={novaNome} 
                  onChange={e => setNovaNome(e.target.value)}
                  disabled={!!geoSugestao && !geoSugestao.id} // Desativa se for registro de cidade localizada
                  placeholder="Nome da cidade" 
                  onBlur={handleGeocodarNova}
                  style={{ 
                    padding:'12px',
                    borderRadius:9,
                    border:'1px solid rgba(255,255,255,0.1)',
                    background: geoSugestao && !geoSugestao.id ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.04)',
                    color: geoSugestao && !geoSugestao.id ? '#64748b' : '#f1f5f9',
                    fontSize:13,
                    outline:'none',
                    cursor: geoSugestao && !geoSugestao.id ? 'not-allowed' : 'text'
                  }} 
                />

                <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginTop: 4 }}>Estado (UF)</label>
                <select 
                  value={novaEstadoId} 
                  onChange={e => setNovaEstadoId(e.target.value)} 
                  onBlur={handleGeocodarNova}
                  disabled={!!geoSugestao && !geoSugestao.id} // Desativa se for registro de cidade localizada
                  style={{ 
                    padding:'12px',
                    borderRadius:9,
                    border:'1px solid rgba(255,255,255,0.1)',
                    background:'#1a1a2e',
                    color: novaEstadoId ? '#f1f5f9' : '#64748b',
                    fontSize:13,
                    outline:'none',
                    cursor: geoSugestao && !geoSugestao.id ? 'not-allowed' : 'pointer'
                  }}
                >
                  {geoSugestao && !geoSugestao.id ? (
                    <option value="">{estados.find(e => e.sigla === geoSugestao.uf && e.redeId === selectedRedeId)?.nome || `Estado ${geoSugestao.uf}`}</option>
                  ) : (
                    <>
                      <option value="">Selecione o estado…</option>
                      {estados
                        .filter(e => e.redeId === selectedRedeId)
                        .map(e => <option key={e.id} value={e.id}>{e.nome} ({e.sigla})</option>)
                      }
                    </>
                  )}
                </select>
              </div>

              {/* Coluna Direita: Mapa Claro */}
              <div style={{ flex: '1.2 1 320px', height: 200, borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', position: 'relative' }}>
                {novaLat && novaLng ? (
                  <MapContainer center={mapCenter} zoom={13} style={{ height: '100%', width: '100%', zIndex: 1 }} scrollWheelZoom={true}>
                    <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" /> {/* Mapa Claro */}
                    <Marker position={mapCenter} icon={markerPinIcon} />
                    <MapCenterController center={mapCenter} />
                    {/* Só permite reposicionar no clique se NÃO for cidade geolocalizada */}
                    {!(geoSugestao && !geoSugestao.id) && (
                      <MapClickEvents onClick={(lat, lng) => {
                        setNovaLat(String(lat));
                        setNovaLng(String(lng));
                      }} />
                    )}
                  </MapContainer>
                ) : (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: 13, background: '#0a0822', padding: 20, textAlign: 'center', boxSizing: 'border-box' }}>
                    Escolha um estado para carregar a posição inicial no mapa…
                  </div>
                )}
                {!(geoSugestao && !geoSugestao.id) && novaLat && novaLng && (
                  <div style={{ position: 'absolute', bottom: 10, left: 10, zIndex: 999, background: 'rgba(0,0,0,0.7)', padding: '4px 8px', borderRadius: 4, fontSize: 10, color: '#a78bfa' }}>
                    💡 Clique no mapa para redefinir a posição
                  </div>
                )}
              </div>
            </div>

            {/* Botoes de Controle - Posicionados abaixo do Mapa, Cancelar mais escuro */}
            <div style={{ display:'flex', gap:10, marginTop:10 }}>
              <button 
                onClick={() => { setShowNova(false); setErro(''); }}
                style={{ 
                  flex:1,
                  padding:'12px',
                  borderRadius:9,
                  border:'1px solid rgba(255,255,255,0.05)',
                  background:'#0b081a', // Muito mais escuro
                  color:'#94a3b8',
                  fontSize:13,
                  fontWeight: 700,
                  cursor:'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#060410'}
                onMouseLeave={e => e.currentTarget.style.background = '#0b081a'}
              >
                Cancelar
              </button>
              <button 
                onClick={handleSalvarNova} 
                disabled={novaSaving}
                style={{ 
                  flex:2.5,
                  padding:'12px',
                  borderRadius:9,
                  border:'none',
                  cursor:novaSaving ? 'not-allowed' : 'pointer',
                  background:'linear-gradient(135deg,#7c3aed,#4f46e5)',
                  color:'#fff',
                  fontSize:13,
                  fontWeight:800,
                  boxShadow: '0 4px 15px rgba(124,58,237,0.3)'
                }}
              >
                {novaSaving ? 'Salvando…' : '✓ Salvar Cidade'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
//#endregion AC_BLOCK cidade-step
